/* AgenticGlobalBar : écrire ou dicter une intention à NOAM depuis n'importe quelle page.
 * Dictée vocale via Web Speech API (même pattern qu'IntelligenceV2). */
import { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Paperclip, Sparkles, GripVertical, Minimize2 } from "lucide-react";
import { api } from "@/lib/api";
import { useTranslation } from "react-i18next";

const POS_KEY = "noam_bar_pos";
const COLLAPSE_KEY = "noam_bar_collapsed";

export default function AgenticGlobalBar({ navigate }) {
  const { t, i18n } = useTranslation();
  const [q, setQ] = useState("");
  const [listening, setListening] = useState(false);
  const [srOk, setSrOk] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === "1");
  const [pos, setPos] = useState(() => {
    try { return JSON.parse(localStorage.getItem(POS_KEY)) || null; } catch { return null; }
  });
  const recRef = useRef(null);
  const fileRef = useRef(null);
  const barRef = useRef(null);
  const dragRef = useRef(null);

  const clampPos = (x, y) => {
    const w = barRef.current?.offsetWidth || 320;
    return {
      x: Math.min(Math.max(8, x), window.innerWidth - w - 8),
      y: Math.min(Math.max(8, y), window.innerHeight - 64),
    };
  };

  const startDrag = (e) => {
    e.preventDefault();
    const rect = barRef.current.getBoundingClientRect();
    dragRef.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top };
    const onMove = (ev) => {
      const p = clampPos(ev.clientX - dragRef.current.dx, ev.clientY - dragRef.current.dy);
      setPos(p);
    };
    const onUp = (ev) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      const p = clampPos(ev.clientX - dragRef.current.dx, ev.clientY - dragRef.current.dy);
      try { localStorage.setItem(POS_KEY, JSON.stringify(p)); } catch { /* noop */ }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const toggleCollapse = (v) => {
    setCollapsed(v);
    try { localStorage.setItem(COLLAPSE_KEY, v ? "1" : "0"); } catch { /* noop */ }
  };

  const onFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    // Transmet les fichiers à l'interface NOAM : upload puis analyse
    window.__noamPendingFiles = files;
    window.__noamIntentConsumed = false;
    const text = q.trim() || t("saas.axp.analyze_default");
    navigate(`/app/intelligence?intent=${encodeURIComponent(text)}`);
    setQ("");
    e.target.value = "";
  };

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    setSrOk(true);
    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = true;
    rec.maxAlternatives = 1;
    rec.lang = { fr: "fr-FR", en: "en-US", de: "de-DE" }[(i18n.language || "fr").slice(0, 2)] || "fr-FR";
    rec.onresult = (event) => {
      let t = "";
      for (let i = event.resultIndex; i < event.results.length; ++i) t += event.results[i][0].transcript;
      setQ(t);
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    recRef.current = rec;
    return () => { try { rec.stop(); } catch { /* noop */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const toggleMic = () => {
    const rec = recRef.current;
    if (!rec) return;
    if (listening) { rec.stop(); setListening(false); }
    else { setQ(""); try { rec.start(); setListening(true); } catch { /* noop */ } }
  };

  const submit = async (e) => {
    e.preventDefault();
    const text = q.trim();
    if (!text) return;
    try {
      const r = await api.post("/workspace/agentic/intent", { text });
      const action = (r.data.actions || [])[0];
      window.__noamIntentConsumed = false;
      if (action?.type === "navigate") navigate(action.to);
      else navigate(`/app/intelligence?intent=${encodeURIComponent(action?.prefill || text)}`);
      setQ("");
    } catch { /* silencieux */ }
  };

  const posStyle = pos
    ? { left: pos.x, top: pos.y, bottom: "auto", transform: "none" }
    : undefined;

  if (collapsed) {
    return (
      <button
        onClick={() => toggleCollapse(false)}
        data-testid="agentic-bar-expand"
        title={t("saas.axp.bar_reopen")}
        style={pos ? { left: pos.x, top: pos.y, bottom: "auto", transform: "none" } : undefined}
        className="fixed bottom-4 left-1/2 -translate-x-1/2 lg:left-[calc(50%+8rem)] z-40 flex items-center justify-center w-11 h-11 rounded-full border border-white/15 bg-[#0b0f18]/95 backdrop-blur shadow-xl shadow-black/40 text-[#378ADD] hover:border-[#378ADD]/60 transition-colors">
        <Sparkles className="w-5 h-5" />
      </button>
    );
  }

  return (
    <form onSubmit={submit} data-testid="agentic-global-bar" ref={barRef}
      style={posStyle}
      className="fixed bottom-4 left-1/2 -translate-x-1/2 lg:left-[calc(50%+8rem)] z-40 w-[min(640px,calc(100vw-2rem))]">
      <div className={`flex items-center gap-2 rounded-full border bg-[#0b0f18]/95 backdrop-blur px-3 py-2.5 shadow-xl shadow-black/40 transition-colors ${listening ? "border-[#EF4444]/60" : "border-white/15"}`}>
        <button type="button" onPointerDown={startDrag} data-testid="agentic-bar-drag"
          title={t("saas.axp.bar_move")} style={{ cursor: "grab", touchAction: "none" }}
          className="p-1 -ml-1 rounded-full text-zinc-500 hover:text-white shrink-0">
          <GripVertical className="w-4 h-4" />
        </button>
        <Sparkles className="w-4 h-4 text-[#378ADD] shrink-0" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="agentic-global-input"
          placeholder={listening ? t("saas.axp.listening") : t("saas.axp.bar_placeholder")}
          className="flex-1 bg-transparent text-sm text-white outline-none placeholder:text-zinc-500"
          autoComplete="off"
        />
        <input ref={fileRef} type="file" multiple className="hidden" data-testid="agentic-global-file-input"
          accept=".pdf,.docx,.doc,.xlsx,.xls,.csv,.txt,.md,.png,.jpg,.jpeg" onChange={onFiles} />
        <button type="button" onClick={() => fileRef.current?.click()} data-testid="agentic-global-attach"
          title={t("saas.axp.attach")}
          className="p-1.5 rounded-full text-zinc-400 hover:text-white hover:bg-white/10 transition-colors">
          <Paperclip className="w-4 h-4" />
        </button>
        {srOk && (
          <button type="button" onClick={toggleMic} data-testid="agentic-global-mic"
            title={listening ? t("saas.axp.mic_stop") : t("saas.axp.mic_start")}
            className={`p-1.5 rounded-full transition-colors ${listening ? "bg-[#EF4444]/20 text-[#EF4444]" : "text-zinc-400 hover:text-white hover:bg-white/10"}`}>
            {listening ? <MicOff className="w-4 h-4 animate-pulse" /> : <Mic className="w-4 h-4" />}
          </button>
        )}
        <button type="button" onClick={() => toggleCollapse(true)} data-testid="agentic-bar-collapse"
          title={t("saas.axp.bar_collapse")}
          className="p-1.5 rounded-full text-zinc-400 hover:text-white hover:bg-white/10 transition-colors">
          <Minimize2 className="w-4 h-4" />
        </button>
      </div>
    </form>
  );
}
